import React, { useContext, useState, useEffect, useRef, useMemo } from 'react';
import { WalletContext } from '../context/WalletContext';
import WalletPickerModal from './WalletPickerModal';
import '../style/components/WalletWatchlist.scss';

const API_BASE = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
  ? 'http://localhost:3003'
  : 'https://api.blockhivetechnologies.com';

function drawNAV(canvas, navSeries) {
  const dpr = window.devicePixelRatio || 1;
  const W = canvas.offsetWidth, H = canvas.offsetHeight;
  if (!W || !H) return;
  canvas.width  = W * dpr;
  canvas.height = H * dpr;
  const ctx = canvas.getContext('2d');
  ctx.scale(dpr, dpr);

  const PAD = { t: 8, r: 8, b: 24, l: 56 };
  const cW = W - PAD.l - PAD.r;
  const cH = H - PAD.t - PAD.b;
  ctx.clearRect(0, 0, W, H);

  const navs = navSeries.map(d => d.nav);
  const minV = Math.min(...navs);
  const maxV = Math.max(...navs);
  const range = maxV - minV || 1;

  const toX = i => PAD.l + (i / Math.max(navSeries.length - 1, 1)) * cW;
  const toY = v => PAD.t + cH - ((v - minV) / range) * cH;

  // Grid
  ctx.strokeStyle = '#1a1a1a'; ctx.lineWidth = 0.5;
  for (let g = 0; g <= 3; g++) {
    const y = PAD.t + (g / 3) * cH;
    ctx.beginPath(); ctx.moveTo(PAD.l, y); ctx.lineTo(PAD.l + cW, y); ctx.stroke();
  }

  const lastNav  = navs[navs.length - 1];
  const firstNav = navs[0];
  const isUp     = lastNav >= firstNav;
  const lineColor = isUp ? '#26c49a' : '#c0392b';
  const fillColor = isUp ? 'rgba(38,196,154,0.09)' : 'rgba(192,57,43,0.09)';

  // Area fill
  ctx.beginPath();
  ctx.moveTo(toX(0), PAD.t + cH);
  navSeries.forEach((d, i) => ctx.lineTo(toX(i), toY(d.nav)));
  ctx.lineTo(toX(navSeries.length - 1), PAD.t + cH);
  ctx.closePath();
  ctx.fillStyle = fillColor;
  ctx.fill();

  // NAV line
  ctx.beginPath();
  navSeries.forEach((d, i) => i === 0 ? ctx.moveTo(toX(i), toY(d.nav)) : ctx.lineTo(toX(i), toY(d.nav)));
  ctx.strokeStyle = lineColor; ctx.lineWidth = 1.5; ctx.stroke();

  // Y-axis labels
  const fmtK = v => v >= 1e6 ? `$${(v/1e6).toFixed(2)}M` : v >= 1000 ? `$${(v/1000).toFixed(1)}K` : `$${v.toFixed(0)}`;
  ctx.font = '8px monospace'; ctx.textAlign = 'right';
  ctx.fillStyle = '#444';
  ctx.fillText(fmtK(maxV), PAD.l - 4, PAD.t + 8);
  ctx.fillText(fmtK(minV), PAD.l - 4, PAD.t + cH - 2);
  ctx.fillStyle = lineColor;
  const curY = toY(lastNav);
  if (curY > PAD.t + 14 && curY < PAD.t + cH - 10) ctx.fillText(fmtK(lastNav), PAD.l - 4, curY + 3);

  // X-axis date labels
  ctx.fillStyle = '#2a2a2a'; ctx.textAlign = 'center'; ctx.font = '7px monospace';
  const pts = [0, Math.floor(navSeries.length / 2), navSeries.length - 1];
  for (const i of pts) {
    if (i < navSeries.length) ctx.fillText(navSeries[i].date.slice(5), toX(i), PAD.t + cH + 16);
  }
}

function NavChart({ navSeries, navLoading }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!canvasRef.current || navSeries.length < 2) return;
    drawNAV(canvasRef.current, navSeries);
  }, [navSeries]);

  const lastNav  = navSeries.length > 0 ? navSeries[navSeries.length - 1].nav : null;
  const firstNav = navSeries.length > 0 ? navSeries[0].nav : null;
  const pct = (lastNav && firstNav) ? ((lastNav - firstNav) / firstNav * 100) : null;

  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        <span style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '1.5px', color: '#333', textTransform: 'uppercase' }}>NAV</span>
        {pct !== null && (
          <span style={{ fontFamily: 'monospace', fontSize: 9, color: pct >= 0 ? '#26c49a' : '#c0392b' }}>
            {pct >= 0 ? '+' : ''}{pct.toFixed(1)}% since deposit
          </span>
        )}
      </div>
      <div style={{ position: 'relative', height: 90, background: '#090909', borderRadius: 2, border: '1px solid #151515' }}>
        <canvas ref={canvasRef} style={{ width: '100%', height: '100%', display: 'block' }} />
        {navLoading && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'monospace', fontSize: 8, color: '#2a2a2a', letterSpacing: '1px' }}>
            computing nav…
          </div>
        )}
        {!navLoading && navSeries.length < 2 && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'monospace', fontSize: 8, color: '#2a2a2a', letterSpacing: '1px' }}>
            no on-chain history
          </div>
        )}
      </div>
    </div>
  );
}

const KNOWN_NAV = new Set(['ETH','WETH','BTC','WBTC','OP','ARB','USDC','USDT','DAI','LINK','UNI','AAVE','MKR','SNX','CRV','LDO','MATIC','BUSD','FRAX']);

function useNAV(address, holdings, prices) {
  const [navSeries,  setNavSeries]  = useState([]);
  const [navLoading, setNavLoading] = useState(false);
  const firedRef = useRef(false);

  useEffect(() => {
    if (!address || !holdings || holdings.length === 0) return;
    if (firedRef.current) return; // only fire once per address

    // Build balance map — include any known symbol with positive balance
    const balMap = {};
    for (const h of holdings) {
      const sym = (h.symbol || '').toUpperCase();
      if (!KNOWN_NAV.has(sym)) continue;
      const bal = parseFloat(h.balance) || 0;
      if (bal > 0) balMap[sym] = (balMap[sym] || 0) + bal;
    }
    if (Object.keys(balMap).length === 0) return;

    // Build blockscoutPrice lookup once to avoid O(n×m) scan in reduce
    const bsPriceMap = {};
    for (const h of holdings) {
      const s = (h.symbol || '').toUpperCase();
      if (s && h.blockscoutPrice) bsPriceMap[s] = h.blockscoutPrice;
    }

    // Wait until wallet has loaded at least $1 of value (ensures Arbitrum has settled)
    const totalUSD = Object.entries(balMap).reduce((sum, [sym, bal]) => {
      const price = prices?.[sym]?.usd || bsPriceMap[sym] || 0;
      return sum + bal * price;
    }, 0);
    if (totalUSD < 1) return;

    firedRef.current = true;

    const balancesParam = Object.entries(balMap)
      .map(([s, v]) => `${s}:${parseFloat(v.toFixed(8))}`)
      .join(',');
    const cacheKey = `nav_v7_${address.toLowerCase()}`;

    try {
      const c = JSON.parse(localStorage.getItem(cacheKey) || 'null');
      if (c && Date.now() - c.ts < 30 * 60 * 1000) { setNavSeries(c.series); return; }
    } catch {}

    setNavLoading(true);
    fetch(`${API_BASE}/portfolio/nav?address=${address}&balances=${encodeURIComponent(balancesParam)}`)
      .then(r => r.json())
      .then(d => {
        const series = d.navSeries || [];
        setNavSeries(series);
        try { localStorage.setItem(cacheKey, JSON.stringify({ series, ts: Date.now() })); } catch {}
      })
      .catch(() => {})
      .finally(() => setNavLoading(false));
  }, [address, holdings, prices]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset fired flag when address changes
  useEffect(() => { firedRef.current = false; setNavSeries([]); }, [address]);

  return { navSeries, navLoading };
}


// Fixed categorical order — matches dashboard dark theme
const PIE_COLORS = ['#00FFE5','#00FF99','#CC33FF','#3366FF','#FFD700','#FF2244','#00FFCC','#FF7700','#BB88FF','#FF9900','#666'];

function buildSegments(holdings, prices) {
  const withValue = holdings
    .filter(h => (h.balance || 0) > 0)
    .map((h, i) => ({
      id: `${h.symbol}-${h.chainId || i}`,
      symbol: h.symbol,
      chain: h.chain || '',
      value: h.balance * (prices[h.symbol]?.usd || h.blockscoutPrice || 0),
    }))
    .sort((a, b) => b.value - a.value);

  const symCount = withValue.reduce((acc, s) => { acc[s.symbol] = (acc[s.symbol] || 0) + 1; return acc; }, {});

  const top = withValue.slice(0, 10).map((s, i) => ({
    ...s,
    label: symCount[s.symbol] > 1 ? `${s.symbol}·${s.chain.slice(0, 3).toUpperCase()}` : s.symbol,
    color: PIE_COLORS[i],
  }));
  // "Other" only for overflow tokens that have real USD value — don't pollute with $0 tokens
  const otherValue = withValue.slice(10).filter(s => s.value > 0).reduce((sum, s) => sum + s.value, 0);
  if (otherValue > 0) top.push({ id: 'other', symbol: 'Other', label: 'Other', value: otherValue, color: PIE_COLORS[10] });
  return top;
}

function donutPath(cx, cy, outerR, innerR, startAngle, endAngle) {
  const c = Math.cos, s = Math.sin;
  const ox1 = cx + outerR * c(startAngle), oy1 = cy + outerR * s(startAngle);
  const ox2 = cx + outerR * c(endAngle),   oy2 = cy + outerR * s(endAngle);
  const ix1 = cx + innerR * c(startAngle), iy1 = cy + innerR * s(startAngle);
  const ix2 = cx + innerR * c(endAngle),   iy2 = cy + innerR * s(endAngle);
  const large = endAngle - startAngle > Math.PI ? 1 : 0;
  return `M${ox1},${oy1} A${outerR},${outerR} 0 ${large} 1 ${ox2},${oy2} L${ix2},${iy2} A${innerR},${innerR} 0 ${large} 0 ${ix1},${iy1} Z`;
}

function DonutChart({ segments, total, formatUSD }) {
  const [hov, setHov] = useState(null);
  const uid = useRef(`ww${Math.random().toString(36).slice(2, 6)}`).current;
  const CX = 80, CY = 80, OR = 62, IR = 38, GAP = 0.02, RING_R = 73;

  const minWeight = total > 0 ? total * 0.025 : 1;
  const displayTotal = segments.reduce((sum, s) => sum + (s.value > 0 ? s.value : minWeight), 0);

  let cum = -Math.PI / 2;
  const arcs = segments.map((seg) => {
    const dispVal = seg.value > 0 ? seg.value : minWeight;
    const sweep = (dispVal / displayTotal) * 2 * Math.PI;
    const s = cum + GAP / 2;
    const e = cum + sweep - GAP / 2;
    const mid = (s + e) / 2;
    cum += sweep;
    const visible = e > s + 0.001;
    return { ...seg, d: visible ? donutPath(CX, CY, OR, IR, s, e) : null, pct: total > 0 ? seg.value / total * 100 : 0, mid };
  });

  const active = hov !== null ? arcs[hov] : null;
  const fmtPct = (pct) => pct < 0.05 ? '<0.1%' : `${pct.toFixed(1)}%`;

  // Radar ring: 48 tick marks at RING_R
  const TICK_N = 48;
  const ticks = Array.from({ length: TICK_N }, (_, i) => {
    const a = (i / TICK_N) * 2 * Math.PI;
    const main = i % 12 === 0, mid = i % 4 === 0;
    return { a, r1: RING_R, r2: RING_R + (main ? 6 : mid ? 4 : 2.5), main, mid };
  });

  // Radar ring: 12 broken arc segments (each 72% of their slot)
  const brokenArcs = Array.from({ length: 12 }, (_, i) => {
    const seg = (2 * Math.PI) / 12;
    return { startA: i * seg - Math.PI / 2, endA: i * seg + seg * 0.72 - Math.PI / 2 };
  });

  // Radar sweep: 55° trailing wedge
  const SWEEP_DEG = 55 * Math.PI / 180;
  const swX = RING_R * Math.sin(SWEEP_DEG);
  const swY = -RING_R * Math.cos(SWEEP_DEG);

  // Orbit particles: different radii, speeds, directions
  const PARTICLES = [
    { r: 67, cls: 'ww-p0', size: 1.5, color: '#37E6CF' },
    { r: 72, cls: 'ww-p1', size: 1.0, color: '#A868FF' },
    { r: 70, cls: 'ww-p2', size: 1.2, color: '#26c49a' },
    { r: 76, cls: 'ww-p3', size: 1.0, color: '#5B84FF' },
  ];

  return (
    <div className="ww-donut-wrap">
      <svg width="160" height="160" viewBox="0 0 160 160" style={{ overflow: 'visible' }}>
        <defs>
          <filter id={`${uid}-glow`} x="-60%" y="-60%" width="220%" height="220%">
            <feGaussianBlur stdDeviation="4" result="b" />
            <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
          <filter id={`${uid}-sm`} x="-40%" y="-40%" width="180%" height="180%">
            <feGaussianBlur stdDeviation="1.5" result="b" />
            <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
        </defs>

        {/* Radar sweep — 4s rotation */}
        <g transform={`translate(${CX},${CY})`}>
          <g className="ww-sweep" style={{ transformOrigin: '0 0' }}>
            <path d={`M0,0 L0,${-RING_R} A${RING_R},${RING_R} 0 0 1 ${swX},${swY} Z`}
              fill="#37E6CF" opacity="0.09" />
            <line x1="0" y1="0" x2="0" y2={-RING_R}
              stroke="#37E6CF" strokeWidth="0.8" opacity="0.5" />
          </g>
        </g>

        {/* Radar ring — 14s rotation */}
        <g transform={`translate(${CX},${CY})`}>
          <g className="ww-ring" style={{ transformOrigin: '0 0' }}>
            {brokenArcs.map((arc, i) => (
              <path key={i}
                d={`M${RING_R * Math.cos(arc.startA)},${RING_R * Math.sin(arc.startA)} A${RING_R},${RING_R} 0 0 1 ${RING_R * Math.cos(arc.endA)},${RING_R * Math.sin(arc.endA)}`}
                fill="none" stroke="#37E6CF" strokeWidth="0.6" opacity="0.3" />
            ))}
            {ticks.map((t, i) => (
              <line key={i}
                x1={t.r1 * Math.cos(t.a - Math.PI / 2)} y1={t.r1 * Math.sin(t.a - Math.PI / 2)}
                x2={t.r2 * Math.cos(t.a - Math.PI / 2)} y2={t.r2 * Math.sin(t.a - Math.PI / 2)}
                stroke={t.main ? '#37E6CF' : t.mid ? '#1C5B52' : '#0d2424'}
                strokeWidth={t.main ? 1 : 0.5}
                opacity={t.main ? 0.9 : t.mid ? 0.5 : 0.3} />
            ))}
          </g>
        </g>

        {/* Orbit particles */}
        {PARTICLES.map((p) => (
          <g key={p.cls} transform={`translate(${CX},${CY})`}>
            <g className={p.cls} style={{ transformOrigin: '0 0' }}>
              <circle cx={0} cy={-p.r} r={p.size}
                fill={p.color} opacity="0.9" filter={`url(#${uid}-sm)`} />
            </g>
          </g>
        ))}

        {/* Breathing donut slices */}
        <g className="ww-breathe" style={{ transformOrigin: `${CX}px ${CY}px` }}>
          {arcs.map((arc, i) => {
            if (!arc.d) return null;
            const isHov = hov === i;
            const dx = isHov ? Math.cos(arc.mid) * 5 : 0;
            const dy = isHov ? Math.sin(arc.mid) * 5 : 0;
            return (
              <path key={arc.id || i} d={arc.d}
                fill={arc.color} fillOpacity={hov === null ? 0.55 : isHov ? 0.82 : 0.25}
                stroke="#050608" strokeWidth="1"
                filter={isHov ? `url(#${uid}-glow)` : undefined}
                opacity={1}
                style={{
                  transform: `translate(${dx}px, ${dy}px)`,
                  transition: 'transform 0.22s ease-out, fill-opacity 0.18s ease-out',
                  cursor: 'default',
                }}
                onMouseEnter={() => setHov(i)} onMouseLeave={() => setHov(null)} />
            );
          })}
        </g>

        {/* Center labels */}
        <g style={{ pointerEvents: 'none' }}>
          <text x={CX} y={CY - 9} textAnchor="middle"
            fill="#758A91" fontSize="6.5" letterSpacing="1.5" fontFamily="monospace">
            {active ? (active.label || active.symbol) : 'PORTFOLIO'}
          </text>
          <text x={CX} y={CY + 6} textAnchor="middle"
            fill="#E8F4F4" fontSize="12" fontWeight="700" fontFamily="monospace">
            {active ? fmtPct(active.pct) : formatUSD(total)}
          </text>
          {active && (
            <text x={CX} y={CY + 20} textAnchor="middle"
              fill="#37E6CF" fontSize="7.5" fontFamily="monospace" opacity="0.85">
              {formatUSD(active.value)}
            </text>
          )}
        </g>
      </svg>

      <div className="ww-legend">
        {arcs.map((arc, i) => (
          <div key={arc.id || i} className={`ww-legend-item${hov === i ? ' hov' : ''}`}
            onMouseEnter={() => setHov(i)} onMouseLeave={() => setHov(null)}>
            <span className="ww-legend-dot" style={{
              background: arc.color,
              boxShadow: hov === i ? `0 0 6px ${arc.color}99` : 'none',
              transition: 'box-shadow 0.2s',
            }} />
            <span className="ww-legend-sym">{arc.label || arc.symbol}</span>
            <span className="ww-legend-pct">{fmtPct(arc.pct)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function useSessionCountdown() {
  const [info, setInfo] = useState({ countdown: '', address: '' });
  useEffect(() => {
    const tick = () => {
      try {
        const token = localStorage.getItem('xyr_dashboard_token');
        if (!token) { setInfo({ countdown: '', address: '' }); return; }
        const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
        const addr = payload.address || '';
        const secsLeft = payload.exp - Math.floor(Date.now() / 1000);
        if (secsLeft <= 0) { setInfo({ countdown: 'expired', address: addr }); return; }
        const h = Math.floor(secsLeft / 3600);
        const m = Math.floor((secsLeft % 3600) / 60);
        const s = secsLeft % 60;
        setInfo({
          countdown: `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`,
          address: addr,
        });
      } catch { setInfo({ countdown: '', address: '' }); }
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, []);
  return info;
}

const WalletWatchlist = () => {
  const {
    connected,
    address,
    holdings,
    prices,
    loading,
    walletSince,
    connectWallet,
    disconnectWallet,
    refreshBalances
  } = useContext(WalletContext);

  const { countdown, address: jwtAddress } = useSessionCountdown();
  const sessionAddress = address || jwtAddress;
  // const { navSeries, navLoading } = useNAV(address, holdings, prices);

  const truncateAddress = (addr) => {
    if (!addr) return '';
    return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
  };

  const copyToClipboard = () => {
    if (address) {
      navigator.clipboard.writeText(address);
      console.log('[WalletWatchlist] Address copied:', address);
    }
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined || isNaN(num)) return '0.0000';
    const n = Number(num);
    if (n === 0) return '0.0000';
    if (n >= 1e12) return `${(n / 1e12).toFixed(2)}T`;
    if (n >= 1e9)  return `${(n / 1e9).toFixed(2)}B`;
    if (n >= 1e6)  return `${(n / 1e6).toFixed(2)}M`;
    if (n >= 1000) return n.toLocaleString('en-US', { maximumFractionDigits: 2 });
    if (n >= 1) return n.toFixed(4);
    if (n >= 0.0001) return n.toFixed(6);
    return n.toFixed(8);
  };

  const formatUSD = (num) => {
    if (num === null || num === undefined || isNaN(num)) return '$0.00';
    const n = Number(num);
    if (n >= 1e12) return `$${(n / 1e12).toFixed(2)}T`;
    if (n >= 1e9)  return `$${(n / 1e9).toFixed(2)}B`;
    if (n >= 1e6)  return `$${(n / 1e6).toFixed(2)}M`;
    return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const getPriceChangeIndicator = (change24h) => {
    if (!change24h || change24h === 0) return null;
    const isPositive = change24h > 0;
    return (
      <span className={isPositive ? 'price-change-positive' : 'price-change-negative'}>
        {isPositive ? '↑' : '↓'}{Math.abs(change24h).toFixed(2)}%
      </span>
    );
  };

  const calculateTotalValue = () => {
    let total = 0;

    holdings.forEach(holding => {
      const balance = holding.balance || 0;
      const price = prices[holding.symbol]?.usd || holding.blockscoutPrice || 0;
      total += balance * price;
    });

    return total;
  };

  // Memoize before any early returns (Rules of Hooks)
  const totalValue = useMemo(() => calculateTotalValue(), [holdings, prices]); // eslint-disable-line react-hooks/exhaustive-deps
  const segments = useMemo(() => buildSegments(holdings, prices), [holdings, prices]);
  const hasHoldings = holdings.length > 0;

  // If not connected, show picker
  const [showPicker, setShowPicker] = useState(false);
  if (!connected) {
    return (
      <div className="wallet-watchlist">
        <div className="wallet-header">
          <span className="watchlist-title">Portfolio</span>
        </div>
        <div className="wallet-disconnected">
          <p>Connect your wallet to view portfolio</p>
          <button
            className="connect-btn"
            onClick={() => setShowPicker(true)}
            disabled={loading}
          >
            {loading ? 'Connecting...' : 'Connect Wallet'}
          </button>
          <WalletPickerModal
            open={showPicker}
            onClose={() => setShowPicker(false)}
            onConnected={() => setShowPicker(false)}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="wallet-watchlist">
      <div className="wallet-header">
        <span className="watchlist-title">Portfolio</span>
        <span className="total-value">{formatUSD(totalValue)}</span>
      </div>
      {(sessionAddress || countdown) && (
        <div className="ww-session">
          <span className="ww-wallet">{sessionAddress ? `${sessionAddress.slice(0,6)}…${sessionAddress.slice(-4)}` : ''}</span>
          {countdown && <span className="ww-countdown">⏱ {countdown}</span>}
        </div>
      )}

      {/* <NavChart navSeries={navSeries} navLoading={navLoading} /> */}

      {loading && !hasHoldings ? (
        <div className="wallet-loading">
          <div className="spinner"></div>
          <span>Loading holdings...</span>
        </div>
      ) : hasHoldings && segments.length > 0 ? (
        <>
          <DonutChart segments={segments} total={totalValue} formatUSD={formatUSD} />
          <div className="wallet-footer">
            <button className="refresh-btn" onClick={() => refreshBalances(null, null, true)} disabled={loading} title="Refresh holdings">
              ↻ Refresh
            </button>
            <button className="disconnect-btn" onClick={disconnectWallet} title="Disconnect wallet">
              ✕ Disconnect
            </button>
          </div>
        </>
      ) : (
        <div className="wallet-empty"><p>No holdings found</p></div>
      )}
    </div>
  );
};

export default WalletWatchlist;
